import $ from 'jquery';
import apiFetch from "@wordpress/api-fetch";
import {
    loadPayPalSdk,
    getFieldValue,
    getRestRoute,
    getRestPath,
    fieldsToJson,
    setFieldValue,
    getPayPalQueryParams,
    getErrorMessage,
    ConfigurationError
} from "@ppcp/utils";
import CheckoutFieldPopulator from "../core/CheckoutFieldPopulator";
import Actions from '@ppcp/actions';

/**
 * Base Controller
 *
 * Orchestrates WooCommerce integration and payment flow.
 * Works with any payment gateway through dependency injection.
 */
class BaseController {

    static attributionDataStored = false;

    constructor({id, settings, cart, context, gateway, container = null, paymentElementSelector = null}) {
        this.id = id;
        this.cart = cart;
        this.gateway = gateway;
        this.context = context;
        this.settings = settings;
        this.container = container ? container : `li.payment_method_${id}`;
        this.paymentElementSelector = paymentElementSelector;
        this.order_field_key = gateway.getOrderFieldKey();
        this.billing_token_key = gateway.getTokenFieldKey();
        this.isPaymentMethodAvailable = true; // Track if payment method can make payments
        this.fields = new CheckoutFieldPopulator(this.context, this); // Initialize field populator
        this.actions = new Actions(); // Shared actions instance (singleton)

        this.gateway.setController(this);
        this.gateway.initialize();
    }

    initialize() {
        // Load PayPal SDK (shared across all gateways)
        this.loadPayPalScript().then(async () => {
            // Render payment elements
            await this.createPaymentElement();
        });
    }

    /**
     * Loads the PayPal JS SDK (shared by PayPal and Google Pay)
     */
    loadPayPalScript() {
        return new Promise((resolve, reject) => {
            loadPayPalSdk(this.getPayPalSDKArgs()).then(paypal => {
                this.paypal = paypal;
                this.gateway.setPayPal(paypal);
                resolve();
            }).catch(error => {
                console.log(error);
                if (error?.code) {
                    this.submitError(getErrorMessage(error));
                }
                reject();
            })
        })
    }

    /**
     * Returns params used to load the PayPal SDK
     */
    getPayPalSDKArgs() {
        return getPayPalQueryParams();
    }

    /**
     * Create payment element(s) using the gateway
     */
    async createPaymentElement() {
        // Reset configuration error from previous calls
        this.configurationError = null;

        // Early return if not available in cart
        if (!this.cart.isPaymentMethodAvailable(this.gateway.id)) {
            this.isPaymentMethodAvailable = false;
            this.onPaymentUnavailable();
            return;
        }

        // Check if gateway can make payments
        let canMakePayment = false;

        try {
            canMakePayment = await this.gateway.canMakePayment();
        } catch (error) {
            canMakePayment = false;
            if (error instanceof ConfigurationError) {
                this.configurationError = error.message;
            } else {
                // Unexpected error - log it and treat as unavailable
                console.error('Payment method availability check failed:', error);
            }
        }

        if (!canMakePayment) {
            this.isPaymentMethodAvailable = false;
            this.onPaymentUnavailable();
            return;
        }

        // Reset availability flag if payment method becomes available again
        this.isPaymentMethodAvailable = true;

        const container = this.getElementContainer();
        if (!container) {
            return;
        }

        if (this.gateway.shouldCreateElement(container)) {
            if (container instanceof NodeList) {
                for (let i = 0; i < container.length; i++) {
                    const node = container[i];
                    await this.gateway.createPaymentElement(node);
                }
            } else {
                await this.gateway.createPaymentElement(container);
            }
            this.$paymentElement = $(container);

            // Called after payment element is created and ready
            this.onPaymentElementReady();

        }
    }

    getPaymentElement() {
        return this.$paymentElement;
    }

    destroyPaymentElement() {
        this.gateway.destroyPaymentElement?.();
    }

    disablePaymentElement(block = false) {
        this.gateway.disablePaymentElement?.();
        if (block && this?.$paymentElement) {
            this.$paymentElement.addClass('disabled');
        }
    }

    enablePaymentElement() {
        this.gateway.enablePaymentElement?.();
        if (this.$paymentElement) {
            this.$paymentElement.removeClass('disabled');
        }
    }

    displayPaymentElement() {
        this.getPaymentElement()?.show();
    }

    hidePaymentElement() {
        this.getPaymentElement()?.hide();
    }

    needsShipping() {
        return this.cart.needsShipping();
    }

    getElementContainer() {
        return null; // Override in subclasses
    }

    /**
     * Called when payment method is unavailable
     * Override in subclasses for context-specific unavailable handling
     */
    onPaymentUnavailable() {
        // Default: do nothing (silently hide payment option)
    }

    /**
     * Called after payment element is created and ready
     * Override in subclasses for context-specific initialization
     */
    onPaymentElementReady() {
        // Override in subclasses if needed
        this.actions.doAction('payment_element_ready', this);
    }

    /**
     * Called by gateway when payment is cancelled
     */
    onCancel() {
        this.enablePaymentElement();
    }

    /**
     * Called by gateway when an error occurs
     */
    onError(error, source) {
        if (this.currentError) {
            this.submitError(this.currentError);
            this.currentError = null;
        } else {
            this.submitError(error);
        }
        this.enablePaymentElement();
        $(document.body).triggerHandler('wc_ppcp_on_error', [this, source, error]);
    }

    fetchBillingToken(token) {
        return apiFetch({
            method: 'GET',
            path: `/wc-ppcp/v1/billing-agreement/token/${token}`
        });
    }

    createPaymentToken(setupTokenId, data = {}) {
        return apiFetch({
            method: 'POST',
            path: `/wc-ppcp/v1/vault/payment-tokens`,
            data: {
                setup_token: setupTokenId,
                ...data
            }
        });
    }

    /**
     * Called by gateway to create order
     * Override in subclasses for context-specific order creation
     */
    createOrder(data, actions) {
        return this.cart.createOrder({...data, payment_method: this.id,}).catch(error => {
            this.currentError = error;
            return error;
        });
    }

    createVaultSetupToken(data = {}) {
        this.disablePaymentElement(true);
        return apiFetch({
            method: 'POST',
            url: getRestPath('/wc-ppcp/v1/vault/setup-tokens'),
            data: {
                context: this.context.getPage(),
                payment_method: this.id,
                ...data
            }
        }).then(token => {
            return token.id;
        }).catch(error => {
            this.currentError = error;
            this.enablePaymentElement();
            throw error;
        });
    }

    onShippingChange(data) {
        return this.cart.updateShipping({
            payment_method: this.id,
            ...data
        }).then(response => {
            return response;
        }).catch(error => {
            throw error;
        })
    }

    onBillingChange(data) {
        return this.cart.updateBilling({
            payment_method: this.id,
            ...data
        }).then(response => {
            return response;
        }).catch(error => {
            throw error;
        })
    }

    submitError(error) {
        // Override in subclasses
    }

    getShippingPrefix() {
        return 'shipping';
    }

    getCartAddress(type, useMap = false) {
        return {
            address_1: getFieldValue('address_1', type, useMap),
            address_2: getFieldValue('address_2', type, useMap),
            state: getFieldValue('state', type, useMap),
            city: getFieldValue('city', type, useMap),
            postcode: getFieldValue('postcode', type, useMap),
            country: getFieldValue('country', type, useMap)
        }
    }

    getCartFullAddress(type, useMap = false) {
        return {
            first_name: getFieldValue('first_name', type, useMap),
            last_name: getFieldValue('last_name', type, useMap),
            address_1: getFieldValue('address_1', type, useMap),
            address_2: getFieldValue('address_2', type, useMap),
            state: getFieldValue('state', type, useMap),
            city: getFieldValue('city', type, useMap),
            postcode: getFieldValue('postcode', type, useMap),
            country: getFieldValue('country', type, useMap)
        }
    }

    /**
     * Called when payment button is clicked
     * Override in subclasses for context-specific click behavior
     */
    onClick() {
        this.storeAttributionValues();
    }

    /**
     * Perform validations
     * @return Promise
     */
    async validate() {
        return true;
    }

    populateCheckoutFields(data) {
        // Delegate to field populator helper
        let options = this.actions.applyFilters('field_population_options', {
            skipShipping: false,
            needsShipping: this.needsShipping()
        }, this);

        this.fields.populate(data, options);

        this.actions.doAction('after_populate_checkout_fields', this);
    }

    /**
     * Submit payment after authorization.
     *
     * Default implementation processes cart checkout via REST API.
     * Override in subclasses for context-specific submission logic:
     * - CheckoutController: Submits WooCommerce checkout form
     * - CartController: Uses default (cart checkout via REST API)
     *
     * @param {Object} checkoutData - Optional standardized checkout data to populate fields before submission
     */
    submit(checkoutData = null) {
        // Populate checkout fields if data provided
        if (checkoutData) {
            this.populateCheckoutFields(checkoutData);
        }

        this.showProcessing();
        return apiFetch({
            method: 'POST',
            url: getRestRoute('cart/checkout'),
            data: this.getCartCheckoutData()
        }).then(response => {
            if (response.result && 'success' == response.result) {
                window.location = response.redirect;
            } else {
                if (response.messages) {
                    return this.submitError(response.messages);
                }
            }
        }).catch(error => {
            if (error.code) {
                this.submitError(error.message);
            }
        });
    }

    getCartCheckoutData() {
        return {payment_method: this.id, context: this.context.getPage(), ...fieldsToJson()};
    }

    getProcessingSelector() {
        return 'body';
    }

    showProcessing() {
        $(this.getProcessingSelector())?.block({
            message: this.getProcessingMessage(),
            overlayCSS: {
                background: '#fff',
                opacity: 0.6
            }
        });
    }

    hideProcessing() {
        $(this.getProcessingSelector())?.unblock();
    }

    getProcessingMessage() {
        return `<div class="wc-ppcp-loader">
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>`;
    }

    getFullName(prefix) {
        const names = [
            getFieldValue('first_name', prefix),
            getFieldValue('last_name', prefix)
        ].filter(Boolean);
        if (names.length == 0) {
            return null;
        } else if (names.length == 1) {
            return names[0];
        }
        return `${names[0]} ${names[1]}`
    }

    updateGatewaySettings(data) {
        const key = `${this.id}_data`;
        if (data[key]) {
            this.gateway.settings = {
                ...this.gateway.settings,
                ...data[key]
            }
        }
    }

    isOrderAttributionEnabled() {
        return typeof wc_order_attribution !== 'undefined' && document.querySelector('wc-order-attribution-inputs') !== null;
    }

    storeAttributionValues() {
        if (BaseController.attributionDataStored) {
            return;
        }
        if (this.context.isCheckout() || !this.isOrderAttributionEnabled()) {
            return;
        }
        const prefix = wc_order_attribution.params.prefix;

        // Store the order attribution values so they will be used in the request.
        $('input[type="hidden"][name^="' + prefix + '"]').each((idx, el) => {
            var $input = $(el);
            var name = $input.attr('name');
            setFieldValue(name, $input.val(), null);
        });

        BaseController.attributionDataStored = true;
    }

    /**
     * Get line items for the current context (cart, product, or order)
     * Returns generic line items that can be mapped to wallet-specific formats
     * @returns {Array} Line items in generic format
     */
    getLineItems() {
        return this.cart.getLineItems();
    }

    /**
     * Get shipping options for the current context
     * Returns generic shipping options that can be mapped to wallet-specific formats
     * @returns {Array} Shipping options in generic format
     */
    getShippingOptions() {
        return this.cart.getShippingOptions();
    }

    /**
     * Get the selected shipping method ID
     * @returns {string} Selected shipping method ID
     */
    getSelectedShippingMethod() {
        return this.cart.getSelectedShippingMethod();
    }

    getCurrency() {
        return this.cart.getCurrency();
    }

    /**
     * Get the total amount for the current context
     * @returns {string} Total amount as a string
     */
    getTotal(type = 'string') {
        return this.cart.getTotal();
    }

    /**
     * @returns {Number} Total amount in cents
     */
    getTotalCents() {
        return this.cart.getTotalCents();
    }

}

export {BaseController};
export default BaseController;
